import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AlertTriangle, Briefcase, ChevronRight, Filter, Plus,
  CalendarDays, ClipboardList,
} from 'lucide-react';
import api from '../utils/api';
import { fmtDate, deadlineClass, statusColor, hasRole } from '../utils/helpers';
import { useAuth } from '../context/AuthContext';
import CountUp from '../components/CountUp';
import toast from 'react-hot-toast';

// ── Helpers ───────────────────────────────────────────────────────────────────

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 2) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days}d ago`;
  return fmtDate(dateStr);
}

function daysUntil(dueDate) {
  if (!dueDate) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const due = new Date(dueDate); due.setHours(0, 0, 0, 0);
  return Math.round((due - today) / 86400000);
}

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

function fmt12(time) {
  if (!time) return '';
  const [h, m] = time.split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
}

const MODULE_ROUTE = { ITR: '/income-tax', TDS: '/tds', GST: '/gst', ROC: '/roc' };

const INR = v => '₹' + Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });

// ── Big number column ─────────────────────────────────────────────────────────
function BigNumber({ value, label, danger, prefix, first }) {
  return (
    <div
      className="px-5 py-1 flex-1 min-w-[130px]"
      style={{ borderLeft: first ? 'none' : '1px solid var(--hairline)' }}
    >
      <div
        className="tnum"
        style={{
          fontSize: '38px', fontWeight: 700, letterSpacing: '-0.03em', lineHeight: 1.15,
          color: danger && value > 0 ? 'var(--c-red)' : 'var(--text-primary)',
        }}
      >
        {prefix}<CountUp value={value} />
      </div>
      <div style={{ fontSize: '12px', fontWeight: 500, color: 'var(--text-muted)', marginTop: '2px' }}>
        {label}
      </div>
    </div>
  );
}

// ── Today deadline row ────────────────────────────────────────────────────────
function DeadlineRow({ item, onClick }) {
  const d = daysUntil(item.due_date);
  const isOverdue = d != null && d < 0;
  const isSoon = d != null && d >= 0 && d <= 5;
  const dateColor = isOverdue ? 'var(--c-red)' : isSoon ? 'var(--c-amber)' : 'var(--text-muted)';
  const subLabel = d == null ? '' : isOverdue ? `${Math.abs(d)} day${Math.abs(d) !== 1 ? 's' : ''} overdue` : d === 0 ? 'due today' : `in ${d} day${d !== 1 ? 's' : ''}`;

  return (
    <div
      onClick={onClick}
      className="group flex items-center gap-3 cursor-pointer"
      style={{ padding: '11px 14px', margin: '0 -14px', borderRadius: 'var(--radius, 14px)' }}
      onMouseEnter={e => { e.currentTarget.style.background = 'var(--surface-hover)'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
    >
      <div className="flex-1 min-w-0">
        <div style={{ fontSize: '14.5px', fontWeight: 600, color: 'var(--text-primary)' }} className="truncate">
          {item.client_name}
        </div>
        <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '1px' }} className="truncate">
          {item.badge || item.type} · {item.period}
          {item.assignee_name && <span style={{ fontSize: '11px' }}> · {item.assignee_name}</span>}
        </div>
      </div>
      <div className="text-right flex-shrink-0">
        <div style={{ fontSize: '13px', fontWeight: 600, color: dateColor }}>
          {fmtDate(item.due_date)}
        </div>
        {subLabel && (
          <div style={{ fontSize: '10.5px', color: dateColor, opacity: 0.85 }}>{subLabel}</div>
        )}
      </div>
      <ChevronRight
        size={15}
        className="flex-shrink-0 transition-transform group-hover:translate-x-0.5"
        style={{ color: 'var(--gray-light)' }}
      />
    </div>
  );
}

// ── Hairline divider ──────────────────────────────────────────────────────────
const Hairline = () => <div style={{ borderTop: '1px solid var(--hairline)' }} />;

// ── Dashboard ─────────────────────────────────────────────────────────────────
export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('warroom');
  const [staffFilter, setStaffFilter] = useState('');
  const [staffList, setStaffList] = useState([]);
  const [meetingStats, setMeetingStats] = useState(null);
  const [todayMeetings, setTodayMeetings] = useState([]);
  const [checklistStats, setChecklistStats] = useState(null);

  const isAdmin = hasRole(user, 'partner', 'senior_staff');

  const load = (sid) => {
    setLoading(true);
    const params = sid ? { staff_id: sid } : {};
    api.get('/dashboard/stats', { params })
      .then(r => setStats(r.data))
      .catch(() => toast.error('Failed to load dashboard'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(staffFilter);
    if (isAdmin) api.get('/auth/users').then(r => setStaffList(r.data));
    const today = new Date().toISOString().split('T')[0];
    api.get('/meetings/stats').then(r => setMeetingStats(r.data)).catch(() => {});
    api.get('/meetings', { params: { from: today, to: today } }).then(r => setTodayMeetings(r.data)).catch(() => {});
    api.get('/checklists/stats').then(r => setChecklistStats(r.data)).catch(() => {});
  }, [staffFilter]);

  if (loading) return (
    <div className="max-w-5xl space-y-8 pt-4">
      <div>
        <div className="skeleton h-4 w-56 mb-4" />
        <div className="skeleton h-12 w-96 mb-2" />
        <div className="skeleton h-12 w-72" />
      </div>
      <div className="flex gap-8">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i}>
            <div className="skeleton h-10 w-16 mb-2" />
            <div className="skeleton h-3 w-20" />
          </div>
        ))}
      </div>
      <div className="space-y-3">
        {Array.from({ length: 5 }).map((_, i) => <div key={i} className="skeleton h-10 w-full" />)}
      </div>
    </div>
  );
  if (!stats) return null;

  const firstName = (user?.name || '').split(' ')[0];
  const dateLine = new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' });
  const overdue = stats.overdueCount || 0;
  const dueToday = stats.dueTodayCount || 0;

  let heroMain, heroSub;
  if (overdue > 0) {
    heroMain = `${overdue} item${overdue !== 1 ? 's' : ''} need${overdue === 1 ? 's' : ''} you.`;
    heroSub = 'Everything else is on track.';
  } else if (dueToday > 0) {
    heroMain = `${dueToday} due today.`;
    heroSub = 'Nothing overdue. Clean slate.';
  } else {
    heroMain = 'All clear.';
    heroSub = `Enjoy the calm, ${firstName}.`;
  }

  return (
    <div className="max-w-6xl">
      {/* ── Top controls row ── */}
      <div className="flex flex-wrap items-center justify-end gap-2 mb-2">
        <div className="flex rounded-lg p-0.5" style={{ background: 'var(--surface-hover)' }}>
          {[
            { key: 'warroom', label: 'Overview' },
            { key: 'mytasks', label: `My Tasks${stats.myTasks?.length ? ` (${stats.myTasks.length})` : ''}` },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className="px-4 py-1.5 rounded-md text-sm font-medium"
              style={tab === t.key
                ? { background: 'var(--surface)', color: 'var(--text-primary)', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }
                : { color: 'var(--text-muted)' }}
            >
              {t.label}
            </button>
          ))}
        </div>
        {isAdmin && tab === 'warroom' && (
          <div className="flex items-center gap-2">
            <Filter size={14} style={{ color: 'var(--text-muted)' }} />
            <select value={staffFilter} onChange={e => setStaffFilter(e.target.value)} className="input-field text-sm py-1.5 w-40">
              <option value="">All Staff</option>
              {staffList.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
        )}
        <Link to="/tasks" className="btn-primary flex items-center gap-1.5 text-sm"><Plus size={15} /> Quick Task</Link>
      </div>

      {/* Inactive staff alert */}
      {isAdmin && stats.inactiveStaffPending?.length > 0 && (
        <div className="rounded-2xl p-4 mb-6" style={{ background: '#FFF5F5', border: '1px solid #FECACA' }}>
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle size={16} style={{ color: 'var(--c-red)' }} />
            <span className="font-semibold text-sm" style={{ color: 'var(--c-red)' }}>Inactive staff with pending tasks</span>
          </div>
          {stats.inactiveStaffPending.map((s, i) => (
            <div key={i} className="text-sm" style={{ color: 'var(--c-red)' }}>
              {s.name} — {s.pending_count} pending task{s.pending_count !== 1 ? 's' : ''} need reassignment.{' '}
              <Link to="/employees" className="underline">Employees →</Link>
            </div>
          ))}
        </div>
      )}

      {/* ── MY TASKS TAB ── */}
      {tab === 'mytasks' && (
        <div className="space-y-6 section-appear">
          <div>
            <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>{dateLine} · {greeting()}, {firstName}</div>
            <h1 style={{ fontSize: '32px', fontWeight: 700, letterSpacing: '-0.03em', color: 'var(--text-primary)', marginTop: '4px' }}>
              My Tasks
            </h1>
          </div>

          {(!stats.myTasks || stats.myTasks.length === 0)
            ? <div className="py-14 text-center" style={{ color: 'var(--text-muted)' }}>
                <Briefcase size={36} className="mx-auto mb-3 opacity-25" />
                <p className="text-sm">No pending tasks assigned to you.</p>
                <Link to="/my-work" className="text-sm font-medium" style={{ color: 'var(--accent)' }}>Full work view →</Link>
              </div>
            : (
              <div>
                {stats.myTasks.map((t, i) => {
                  const cls = deadlineClass(t.due_date);
                  return (
                    <div key={i}>
                      {i > 0 && <Hairline />}
                      <div
                        className="flex items-center gap-3"
                        style={{ padding: '11px 14px', margin: '0 -14px', borderRadius: 'var(--radius, 14px)' }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'var(--surface-hover)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                      >
                        <div className="flex-1 min-w-0">
                          <div style={{ fontSize: '14.5px', fontWeight: 600, color: 'var(--text-primary)' }}>{t.title}</div>
                          <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '1px' }}>
                            {t.client_name ? `${t.client_name} · ` : ''}{t.status}
                            {t.priority === 'High' && <span style={{ color: 'var(--c-red)' }}> · High priority</span>}
                            {t.delegated_name && ` · → ${t.delegated_name}`}
                          </div>
                        </div>
                        <div
                          className="text-xs font-semibold flex-shrink-0"
                          style={{ color: cls === 'overdue' ? 'var(--c-red)' : cls === 'due-today' ? 'var(--c-amber)' : 'var(--text-muted)' }}
                        >
                          {cls === 'overdue' ? 'OVERDUE' : cls === 'due-today' ? 'TODAY' : fmtDate(t.due_date)}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div className="mt-4">
                  <Link to="/my-work" className="text-sm font-medium" style={{ color: 'var(--accent)' }}>
                    Full work view — ITR, TDS, GST &amp; more →
                  </Link>
                </div>
              </div>
            )}
        </div>
      )}

      {/* ── OVERVIEW (WAR ROOM) TAB ── */}
      {tab === 'warroom' && (
        <>
          {/* A. Hero */}
          <div className="section-appear pt-2 pb-8">
            <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
              {dateLine} · {greeting()}, {firstName}
            </div>
            <h1 style={{
              fontSize: 'clamp(36px, 5vw, 50px)', fontWeight: 700,
              letterSpacing: '-0.035em', lineHeight: 1.08,
              color: 'var(--text-primary)', marginTop: '10px',
            }}>
              {heroMain}
              <br />
              <span style={{ color: 'var(--gray-light)' }}>{heroSub}</span>
            </h1>
          </div>

          {/* B. Big numbers row */}
          <div className="section-appear flex flex-wrap -mx-5 pb-10" style={{ animationDelay: '60ms' }}>
            <BigNumber first value={overdue} label="Overdue" danger />
            <BigNumber value={dueToday} label="Due today" />
            <BigNumber value={stats.totalClients} label="Active clients" />
            <BigNumber value={Math.round(stats.billedThisMonth || 0)} label="Billed this month" prefix="₹" />
          </div>

          {/* Quiet alert links */}
          {(meetingStats?.pending_actions > 0 || meetingStats?.mom_pending > 0 || checklistStats?.pending_docs > 0 || stats.internalDeadlineAlert > 0) && (
            <div className="section-appear flex flex-wrap gap-x-5 gap-y-1 pb-8" style={{ fontSize: '12.5px', color: 'var(--text-muted)', animationDelay: '90ms' }}>
              {stats.internalDeadlineAlert > 0 && (
                <span><span style={{ color: 'var(--c-amber)', fontWeight: 600 }}>{stats.internalDeadlineAlert}</span> past internal deadline</span>
              )}
              {meetingStats?.pending_actions > 0 && (
                <Link to="/meetings/action-items" className="hover:underline">
                  <span style={{ color: 'var(--c-amber)', fontWeight: 600 }}>{meetingStats.pending_actions}</span> pending action{meetingStats.pending_actions !== 1 ? 's' : ''}
                </Link>
              )}
              {meetingStats?.mom_pending > 0 && (
                <Link to="/meetings" className="hover:underline">
                  <span style={{ color: 'var(--c-red)', fontWeight: 600 }}>{meetingStats.mom_pending}</span> MOM{meetingStats.mom_pending !== 1 ? 's' : ''} pending
                </Link>
              )}
              {checklistStats?.pending_docs > 0 && (
                <Link to="/checklists" className="hover:underline">
                  <span style={{ color: 'var(--c-amber)', fontWeight: 600 }}>{checklistStats.pending_docs}</span> document{checklistStats.pending_docs !== 1 ? 's' : ''} pending from clients
                </Link>
              )}
            </div>
          )}

          {/* C + D: Today section and right rail */}
          <div className="section-appear grid grid-cols-1 lg:grid-cols-3 gap-x-14 gap-y-10 pb-10" style={{ animationDelay: '120ms' }}>
            {/* Today (left, 2 cols) */}
            <div className="lg:col-span-2">
              <div className="flex items-baseline justify-between mb-2">
                <h2 style={{ fontSize: '19px', fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--text-primary)' }}>Today</h2>
                <Link to="/calendar" className="text-sm font-medium" style={{ color: 'var(--accent)' }}>
                  All {stats.upcoming?.length || 0} →
                </Link>
              </div>
              {(!stats.upcoming || stats.upcoming.length === 0)
                ? <p className="text-sm py-6" style={{ color: 'var(--text-muted)' }}>Nothing on the horizon. All caught up.</p>
                : (
                  <div>
                    {stats.upcoming.slice(0, 8).map((item, i) => (
                      <div key={i}>
                        {i > 0 && <Hairline />}
                        <DeadlineRow item={item} onClick={() => navigate(MODULE_ROUTE[item.badge] || '/calendar')} />
                      </div>
                    ))}
                  </div>
                )}

              {/* E. Today's meetings — same row style */}
              {todayMeetings.length > 0 && (
                <div className="mt-10">
                  <div className="flex items-baseline justify-between mb-2">
                    <h2 style={{ fontSize: '19px', fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--text-primary)' }}>Meetings</h2>
                    <Link to="/meetings" className="text-sm font-medium" style={{ color: 'var(--text-muted)' }}>All meetings →</Link>
                  </div>
                  <div>
                    {todayMeetings.map((m, i) => (
                      <div key={m.id}>
                        {i > 0 && <Hairline />}
                        <div
                          onClick={() => navigate(`/meetings/${m.id}`)}
                          className="group flex items-center gap-3 cursor-pointer"
                          style={{ padding: '11px 14px', margin: '0 -14px', borderRadius: 'var(--radius, 14px)' }}
                          onMouseEnter={e => { e.currentTarget.style.background = 'var(--surface-hover)'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                        >
                          <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', width: '72px', flexShrink: 0 }}>
                            {m.meeting_time ? fmt12(m.meeting_time) : '—'}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div style={{ fontSize: '14.5px', fontWeight: 600, color: 'var(--text-primary)' }} className="truncate">{m.title}</div>
                            <div style={{ fontSize: '12px', color: 'var(--text-muted)' }} className="truncate">
                              {m.client_name || 'No client'} · {m.mode}{m.assigned_to_name ? ` · ${m.assigned_to_name}` : ''}
                            </div>
                          </div>
                          <div className="flex items-center gap-1.5 flex-shrink-0" style={{ fontSize: '12px', fontWeight: 500, color: 'var(--text-muted)' }}>
                            <span style={{
                              width: '6px', height: '6px', borderRadius: '50%', display: 'inline-block',
                              background: m.status === 'Completed' ? 'var(--c-green)' : 'var(--gray-light)',
                            }} />
                            {m.status}
                          </div>
                          <ChevronRight size={15} className="flex-shrink-0 transition-transform group-hover:translate-x-0.5" style={{ color: 'var(--gray-light)' }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Right rail */}
            <div>
              <h2 style={{ fontSize: '19px', fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--text-primary)', marginBottom: '8px' }}>Activity</h2>
              {(!stats.activity || stats.activity.length === 0)
                ? <p className="text-sm py-4" style={{ color: 'var(--text-muted)' }}>No recent activity.</p>
                : (
                  <div>
                    {stats.activity.slice(0, 10).map((a, i) => (
                      <div key={i}>
                        {i > 0 && <Hairline />}
                        <div style={{ padding: '9px 0' }}>
                          <div style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-primary)', lineHeight: 1.4 }}>
                            {a.user_name} {a.action}
                            {a.entity_name ? ` — ${a.entity_name}` : ''}
                          </div>
                          <div style={{ fontSize: '11.5px', color: 'var(--text-muted)', marginTop: '1px' }}>
                            {timeAgo(a.created_at)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

              {/* Unbilled work block */}
              <Link
                to="/billing"
                className="block mt-8"
                style={{
                  background: 'var(--surface-hover)', borderRadius: 'var(--radius-lg, 16px)',
                  padding: '18px 20px', textDecoration: 'none',
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.015)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
              >
                <div style={{ fontSize: '12px', fontWeight: 500, color: 'var(--text-muted)' }}>Unbilled work</div>
                <div className="tnum" style={{ fontSize: '26px', fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--text-primary)', margin: '4px 0 6px' }}>
                  ₹<CountUp value={Math.round(stats.unbilledAmount || 0)} />
                </div>
                <div style={{ fontSize: '13px', fontWeight: 500, color: 'var(--accent)' }}>Generate invoices →</div>
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
